// Product settings > Timeline tab: every change detected on this product's
// pages (the brand places, removes, replaces or moves videos in their own
// platform; we see it), newest first, each with who did it, when it was
// seen, and its measured outcome against what was predicted. Expanding a
// row fetches the intervention detail (metric comparison + verification
// history). "I removed it" records a removal expectation the next page
// check confirms; "Log a change" records a change we cannot see. An
// Observations accordion shows the raw looks per page.
import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import axiosInstant from '../../../../api';
import {
  interventionEndpoint,
  interventionExpectRemovalEndpoint,
  productAutopilotPageObservationsEndpoint,
  productPageChangeEndpoint,
} from '../../../../api/endpoints';
import { ConfirmationDialog } from '../../../../components/ConfirmationDialog/ConfirmationDialog';
import { DotLottleLoader } from '../../../../components/Loader';
import { apiErrorCode } from '../../../../utils/apiError';
import { IconChevronDown } from '../../../../utils/icons';
import { AutopilotEpisode, AutopilotIntervention, InterventionDetail, PageObservation } from '../../../../types/autopilot';
import {
  ACTION_LABELS,
  CHANGE_KIND_LABELS,
  DETECTION_SOURCE_LABELS,
  EXECUTOR_LABELS,
  INTERVENTION_SOURCE_LABELS,
  INTERVENTION_STATUS_LABELS,
  INTERVENTION_TYPE_LABELS,
  LEARNING_STATUS_LABELS,
  METRIC_LABELS,
  metricLabel,
  OBSERVATION_SOURCE_LABELS,
  Pill,
  VerdictArrow,
  WINDOW_LABELS,
  btnGhost,
  btnPrimary,
  btnSecondary,
  fmtDate,
  fmtDelta,
  fmtMeasurement,
  fmtNum,
  humanize,
  statusTone,
} from './autopilotLabels';
import { ProductAutopilotState } from './useProductAutopilot';

interface Props {
  desireid: number;
  brandId: number | null;
  state: ProductAutopilotState;
}

const hostOf = (iv: AutopilotIntervention): string => {
  const raw = iv.pdp_url && iv.pdp_url.pdp_url;
  if (!raw) return '';
  try {
    return new URL(raw).hostname.replace(/^www\./, '');
  } catch {
    return String(raw).replace(/^https?:\/\//, '').split('/')[0];
  }
};

const verificationTone = (status: string | null): 'good' | 'bad' | 'neutral' => {
  if (status === 'verified') return 'good';
  if (status === 'missing' || status === 'error') return 'bad';
  return 'neutral';
};

// "I removed it" applies to a video we detected on the page and still count there.
const removable = (iv: AutopilotIntervention) =>
  (iv.subject === 'video' || iv.intervention_type === 'place_video')
  && iv.change_kind !== 'video_removed'
  && (iv.status === 'active' || iv.status === 'completed');

const changeLabel = (iv: AutopilotIntervention): string =>
  iv.change_kind ? humanize(iv.change_kind, CHANGE_KIND_LABELS) : humanize(iv.intervention_type, INTERVENTION_TYPE_LABELS);

// "seen between 3 Oct and 5 Oct" — the interval the change happened in.
const seenBetween = (iv: AutopilotIntervention): string | null => {
  if (!iv.observed_present_at) return null;
  if (iv.observed_absent_at) {
    const a = fmtDate(iv.observed_absent_at);
    const b = fmtDate(iv.observed_present_at);
    return a === b ? `seen ${b}` : `seen between ${a} and ${b}`;
  }
  return `seen ${fmtDate(iv.observed_present_at)}`;
};

function SummaryLine({ iv }: { iv: AutopilotIntervention }) {
  const s = iv.summary;
  if (iv.status === 'suspected') {
    return iv.change_kind === 'video_removed'
      ? <span className="text-[12px] text-muted">Missing from one page check — confirmed if the next one agrees</span>
      : <span className="text-[12px] text-muted">Seen by the tag — confirmed when a page check agrees</span>;
  }
  if (iv.status === 'retracted') {
    return iv.change_kind === 'video_removed'
      ? <span className="text-[12px] text-muted">Reappeared on the next page check</span>
      : <span className="text-[12px] text-muted">Reported by the tag, never seen by a page check</span>;
  }
  if (!s) return <span className="text-[12px] text-muted">Not measured yet</span>;
  const metric = s.headline_metric || s.primary_metric;
  const se = (s as any).se as number | null | undefined;
  const nPost = (s as any).n_post as number | null | undefined;
  return (
    <Box className="flex items-center gap-2 flex-wrap text-[12px] text-text-2">
      <VerdictArrow verdict={s.verdict} />
      {metric ? <span className="text-muted">{humanize(metric, METRIC_LABELS)}</span> : null}
      <span title={se != null ? 'The ± is the measurement uncertainty from repeated analyses' : undefined}>
        {se != null || nPost != null
          ? fmtMeasurement({ baseline: s.baseline, value: s.latest, delta: s.delta, se, n_post: nPost, n_base: iv.n_base })
          : <>{fmtNum(s.baseline, 1)} → <span className="font-semibold text-text-1">{fmtNum(s.latest, 1)}</span> ({fmtDelta(s.delta)})</>}
        {s.predicted_delta != null ? <span> · predicted {fmtDelta(s.predicted_delta)}</span> : null}
      </span>
      {s.confounded ? (
        <Pill tone="warn" title="Another change that can move this metric overlapped this one, so the headline cannot be credited to this change alone. See the episode above it.">
          Confounded
        </Pill>
      ) : null}
      {s.confounded && s.sections_clean ? (
        <span className="text-muted" title="Sections no overlapping change could have moved: these measurements are this change's own">
          {s.sections_clean} of {s.sections_total} sections measured cleanly
        </span>
      ) : null}
    </Box>
  );
}

// The episode card: the page's score before the first change against the
// newest analysis after the last, with the changes listed under it.
function EpisodeCard({
  episode,
  members,
  onRemove,
}: {
  episode: AutopilotEpisode;
  members: AutopilotIntervention[];
  onRemove: (iv: AutopilotIntervention) => void;
}) {
  const [open, setOpen] = useState(false);
  const host = episode.pdp_url ? hostOf({ pdp_url: episode.pdp_url } as AutopilotIntervention) : null;
  const sameDay = dayjs(episode.started_at).isSame(episode.ended_at, 'day');
  const when = sameDay ? `on ${fmtDate(episode.started_at)}` : `between ${fmtDate(episode.started_at)} and ${fmtDate(episode.ended_at)}`;
  const b = episode.baseline;
  const l = episode.latest;
  return (
    <Box className="rounded-[12px] border border-accent/40 bg-surface px-3 py-2">
      <Box className="flex items-start justify-between gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer" aria-expanded={open}>
          <Box className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-text-1">
              {episode.count} changes {when}
            </span>
            <Pill tone="accent" title="Changes made too close together to credit any one of them; the page is measured as a whole">Episode</Pill>
            {host ? <span className="text-[12px] text-text-2">{host}</span> : null}
          </Box>
          <Box className="flex items-center gap-2 flex-wrap text-[12px] text-text-2 mt-1">
            {b && l ? (
              <>
                <VerdictArrow verdict={episode.verdict} />
                <span className="text-muted">PDP score</span>
                <span>
                  {fmtNum(b.overall)} → <span className="font-semibold text-text-1">{fmtNum(l.overall)}</span> ({fmtDelta(episode.delta)})
                  {episode.predicted_delta != null ? <span> · predicted {fmtDelta(episode.predicted_delta)} combined</span> : null}
                </span>
                <span className="text-muted">
                  Human {fmtNum(b.human)} → {fmtNum(l.human)} · AI {fmtNum(b.agentic)} → {fmtNum(l.agentic)}
                </span>
              </>
            ) : null}
            {b && !l ? (
              <span className="text-muted">
                Score {fmtNum(b.overall)} before the first change · awaiting the next analysis after the last one
              </span>
            ) : null}
            {!b ? <span className="text-muted">No baseline before the first change</span> : null}
          </Box>
          <span className="block text-[11px] text-muted mt-1">
            Each change below is measured on the sections only it could have moved; the page-wide numbers belong to the episode.
          </span>
        </button>
        <button type="button" onClick={() => setOpen((v) => !v)} className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0" aria-label={open ? 'Collapse' : 'Expand'}>
          <IconChevronDown size={14} open={open} />
        </button>
      </Box>
      {open ? (
        <Box className="flex flex-col gap-2 mt-2 pl-2 border-l-2 border-border-token">
          {members.map((iv) => (
            <InterventionRow key={iv.id} iv={iv} onRemove={onRemove} />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

// The literal change: the sentences the page gained and lost, or the image.
function WhatChanged({ detail }: { detail: InterventionDetail }) {
  const d = detail.content_diff;
  if (detail.image_key) {
    const src = /^https?:\/\//i.test(detail.image_key) ? detail.image_key : `https://${detail.image_key}`;
    return (
      <Box>
        <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">What changed</span>
        <Box className="flex items-center gap-3 mt-1">
          <img src={src} alt="" className="w-16 h-16 object-cover rounded-[8px] border border-border-token bg-surface-2" loading="lazy" />
          <span className="text-[12px] text-text-2 break-all">
            {detail.change_kind === 'image_removed' ? 'Removed from the gallery: ' : 'Added to the gallery: '}
            <span className="text-muted">{detail.image_key}</span>
          </span>
        </Box>
      </Box>
    );
  }
  if (!d || (!d.added.length && !d.removed.length)) return null;
  const more = (shown: number, total: number) => (total > shown ? <li className="list-none text-muted">+{total - shown} more</li> : null);
  return (
    <Box>
      <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">What changed</span>
      {detail.content_summary ? <p className="text-[12px] text-text-2 mt-1 mb-1">{detail.content_summary}</p> : null}
      <Box className="grid gap-3 mt-1" style={{ gridTemplateColumns: d.added.length && d.removed.length ? '1fr 1fr' : '1fr' }}>
        {d.added.length ? (
          <Box>
            <span className="text-[11px] text-up font-semibold">Added ({d.added_count})</span>
            <ul className="m-0 mt-1 pl-0 list-none flex flex-col gap-[3px] text-[12px] text-text-2">
              {d.added.map((s) => (
                <li key={s} className="border-l-2 border-up/50 pl-2">{s}</li>
              ))}
              {more(d.added.length, d.added_count)}
            </ul>
          </Box>
        ) : null}
        {d.removed.length ? (
          <Box>
            <span className="text-[11px] text-down font-semibold">Removed ({d.removed_count})</span>
            <ul className="m-0 mt-1 pl-0 list-none flex flex-col gap-[3px] text-[12px] text-text-2">
              {d.removed.map((s) => (
                <li key={s} className="border-l-2 border-down/50 pl-2 line-through decoration-down/40">{s}</li>
              ))}
              {more(d.removed.length, d.removed_count)}
            </ul>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function DetailBlock({ detail }: { detail: InterventionDetail }) {
  const rows = detail.comparison || [];
  const checks = detail.verifications || [];
  return (
    <Box className="flex flex-col gap-3 pt-3 mt-2 border-t border-border-token">
      <WhatChanged detail={detail} />
      <Box>
        <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">Predicted vs measured</span>
        {rows.length ? (
          <Box className="overflow-x-auto mt-1">
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr className="text-muted text-left">
                  <th className="font-semibold py-1 pr-3">Metric</th>
                  <th className="font-semibold py-1 pr-3">Baseline</th>
                  <th className="font-semibold py-1 pr-3">Predicted</th>
                  <th className="font-semibold py-1 pr-3">Measured</th>
                  <th className="font-semibold py-1">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.metric_key} className="border-t border-border-token text-text-2">
                    <td className="py-1 pr-3">
                      <span className="text-text-1">{metricLabel(r.metric_key)}</span>
                      {r.role === 'primary' ? <span className="text-muted"> · primary</span> : null}
                      {r.confounded === true ? (
                        <span className="text-[11px] text-[#A0662E] dark:text-accent-text" title="An overlapping change could have moved this metric too"> · confounded</span>
                      ) : null}
                      {r.confounded === false && r.actual_delta != null ? (
                        <span className="text-[11px] text-up" title="No overlapping change could have moved this metric: this measurement is this change's own"> · clean</span>
                      ) : null}
                    </td>
                    <td className="py-1 pr-3">{fmtNum(r.baseline_value, 1)}</td>
                    <td className="py-1 pr-3">
                      {r.predicted_delta != null ? fmtDelta(r.predicted_delta) : '—'}
                      {r.band ? <span className="text-muted"> ({fmtDelta(r.band[0])} to {fmtDelta(r.band[1])})</span> : null}
                    </td>
                    <td className="py-1 pr-3">
                      {r.actual_delta != null ? (
                        <>
                          {fmtDelta(r.actual_delta)}
                          {r.actual_value != null ? <span className="text-muted"> → {fmtNum(r.actual_value, 1)}</span> : null}
                        </>
                      ) : (
                        <span className="text-muted">{r.availability === 'unavailable' ? 'Not available' : 'Pending'}</span>
                      )}
                    </td>
                    <td className="py-1">
                      <VerdictArrow verdict={r.verdict} />
                      {r.window ? <span className="text-muted"> {humanize(r.window, WINDOW_LABELS)}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        ) : (
          <p className="text-[12px] text-muted mt-1 mb-0">No metrics to compare for this change.</p>
        )}
      </Box>
      <Box>
        <span className="text-[11px] font-bold text-muted uppercase tracking-[0.4px]">Verification history</span>
        {checks.length ? (
          <ul className="m-0 mt-1 pl-0 list-none flex flex-col gap-[2px] text-[12px] text-text-2">
            {checks.map((v) => (
              <li key={v.id} className="flex items-center gap-2 flex-wrap">
                <span className="text-muted">{v.checked_at ? dayjs(v.checked_at).format('D MMM YYYY HH:mm') : '—'}</span>
                <Pill tone={verificationTone(v.status)}>
                  {humanize(v.status, {})}
                </Pill>
                {v.source ? <span className="text-muted">{humanize(v.source, {})}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] text-muted mt-1 mb-0">No verification checks yet.</p>
        )}
      </Box>
      {detail.note ? <p className="text-[12px] text-text-2 m-0">Note: {detail.note}</p> : null}
    </Box>
  );
}

function InterventionRow({
  iv,
  onRemove,
}: {
  iv: AutopilotIntervention;
  onRemove: (iv: AutopilotIntervention) => void;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<InterventionDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && !loadingDetail) {
      setLoadingDetail(true);
      try {
        const { data } = await axiosInstant.get<InterventionDetail>(interventionEndpoint(iv.id));
        setDetail(data);
      } catch {
        toast.error('Couldn\'t load the details for this change.');
        setOpen(false);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const host = hostOf(iv);
  const seen = seenBetween(iv);
  return (
    <Box className="rounded-[12px] border border-border-token bg-surface px-3 py-2">
      <Box className="flex items-start justify-between gap-2">
        <button type="button" onClick={toggle} className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer" aria-expanded={open}>
          <Box className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-text-1">{changeLabel(iv)}</span>
            {iv.subject === 'video' && iv.video_ours != null ? (
              <Pill tone={iv.video_ours ? 'accent' : 'neutral'} title={iv.video_ours ? 'One of the expert videos' : 'A video that is not one of ours'}>
                {iv.video_ours ? 'Expert video' : 'Other video'}
              </Pill>
            ) : null}
            <Pill tone={statusTone(iv.status)}>{humanize(iv.status, INTERVENTION_STATUS_LABELS)}</Pill>
            {iv.recommendation_id ? (
              <Pill tone="good" title={iv.fulfilled_recommendation_ids && iv.fulfilled_recommendation_ids.length > 1 ? `This change fulfilled ${iv.fulfilled_recommendation_ids.length} recommendations` : 'This change fulfilled a recommendation'}>
                Fulfilled{iv.fulfilled_recommendation_ids && iv.fulfilled_recommendation_ids.length > 1 ? ` ×${iv.fulfilled_recommendation_ids.length}` : ''}
              </Pill>
            ) : null}
            {iv.content_kinds && iv.content_kinds.length
              ? iv.content_kinds.map((k) => (
                <Pill
                  key={k.solve_type}
                  tone={iv.analysis_confirmed && iv.analysis_confirmed[k.solve_type] ? 'good' : 'neutral'}
                  title={`${k.direction} · ${Math.round(k.confidence * 100)}% from the page text${iv.analysis_confirmed && iv.analysis_confirmed[k.solve_type] ? ' · confirmed by the next analysis' : ''}${k.evidence ? ` · "${k.evidence}"` : ''}`}
                >
                  {humanize(k.solve_type, ACTION_LABELS)}
                </Pill>
              ))
              : null}
            {iv.change_kind === 'copy_changed' && iv.content_kinds && !iv.content_kinds.length ? (
              <span className="text-[11px] text-muted" title={iv.content_summary || undefined}>no content kind recognised</span>
            ) : null}
            <span className="text-[11px] text-muted">
              {iv.executor && EXECUTOR_LABELS[iv.executor] ? EXECUTOR_LABELS[iv.executor] : humanize(iv.source, INTERVENTION_SOURCE_LABELS)}
            </span>
            {host ? <span className="text-[12px] text-text-2">{host}</span> : null}
            {iv.objective && iv.objective.name ? <Pill tone="accent">{iv.objective.name}</Pill> : null}
          </Box>
          <Box className="flex items-center gap-3 flex-wrap text-[11px] text-muted mt-1">
            {iv.file ? <span>Video: {iv.file.name || `#${iv.file.id}`}</span> : null}
            {seen ? (
              <span title={iv.detection_source ? humanize(iv.detection_source, DETECTION_SOURCE_LABELS) : undefined}>
                {seen}
                {iv.detection_source && DETECTION_SOURCE_LABELS[iv.detection_source] ? ` · ${DETECTION_SOURCE_LABELS[iv.detection_source]}` : ''}
              </span>
            ) : (
              <span>{iv.activated_at ? `Detected ${fmtDate(iv.activated_at)}` : `Created ${fmtDate(iv.created_at)}`}</span>
            )}
            {iv.position_before != null && iv.position_after != null ? <span>position {iv.position_before + 1} → {iv.position_after + 1}</span> : null}
            {iv.verified_at && iv.detection_source === 'tag' ? <span>Confirmed by a page check {fmtDate(iv.verified_at)}</span> : null}
            {iv.deactivated_at && iv.status !== 'retracted' ? <span>Closed {fmtDate(iv.deactivated_at)}</span> : null}
          </Box>
          <Box className="mt-1">
            <SummaryLine iv={iv} />
          </Box>
          {iv.learning_status ? (
            <span className="block text-[11px] text-muted mt-1">{humanize(iv.learning_status, LEARNING_STATUS_LABELS)}</span>
          ) : null}
        </button>
        <Box className="flex items-center gap-1 shrink-0">
          {removable(iv) ? (
            <button type="button" className={btnSecondary} onClick={() => onRemove(iv)} title="Tell us you took this video off the page; the next page check confirms it">
              I removed it
            </button>
          ) : null}
          <button type="button" onClick={toggle} className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2" aria-label={open ? 'Collapse' : 'Expand'}>
            <IconChevronDown size={14} open={open} />
          </button>
        </Box>
      </Box>
      {open ? (
        <>
          {loadingDetail ? <p className="text-[12px] text-muted mt-2 mb-0">Loading…</p> : null}
          {detail ? <DetailBlock detail={detail} /> : null}
        </>
      ) : null}
    </Box>
  );
}

export function ProductTimelinePanel({ desireid, brandId, state }: Props) {
  const { data, loading, error, reload } = state;
  const [removing, setRemoving] = useState<AutopilotIntervention | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logPage, setLogPage] = useState('');
  const [logNote, setLogNote] = useState('');
  const [logDate, setLogDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [logBusy, setLogBusy] = useState(false);

  const pages = (data && data.pages) || [];
  const interventions = ((data && data.interventions) || []).slice().sort((a, b) => b.id - a.id);
  // Episodes headline their changes; everything else stands on its own.
  // Newest first, an episode placed by its newest change.
  const episodes = (data && data.episodes) || [];
  const inEpisode = new Map<number, AutopilotEpisode>();
  episodes.forEach((ep) => ep.intervention_ids.forEach((id) => inEpisode.set(id, ep)));
  const seenEpisode = new Set<string>();
  const timeline: Array<{ kind: 'episode'; episode: AutopilotEpisode; members: AutopilotIntervention[] } | { kind: 'change'; iv: AutopilotIntervention }> = [];
  interventions.forEach((iv) => {
    const ep = inEpisode.get(iv.id);
    if (!ep) {
      timeline.push({ kind: 'change', iv });
      return;
    }
    if (seenEpisode.has(ep.key)) return;
    seenEpisode.add(ep.key);
    timeline.push({ kind: 'episode', episode: ep, members: interventions.filter((x) => ep.intervention_ids.indexOf(x.id) >= 0) });
  });

  const openLog = () => {
    setLogPage(pages.length === 1 ? String(pages[0].id) : '');
    setLogNote('');
    setLogDate(dayjs().format('YYYY-MM-DD'));
    setLogOpen(true);
  };

  const submitLog = async () => {
    if (!logPage) {
      toast.error('Pick the product page that changed.');
      return;
    }
    if (!logNote.trim()) {
      toast.error('Describe what changed.');
      return;
    }
    setLogBusy(true);
    try {
      await axiosInstant.post(productPageChangeEndpoint(desireid, brandId), {
        pdp_url_id: Number(logPage),
        note: logNote.trim(),
        changed_at: logDate ? dayjs(logDate).toISOString() : undefined,
      });
      toast.success('Change logged.');
      setLogOpen(false);
      await reload(true);
    } catch (e: any) {
      if (apiErrorCode(e) === 'InvalidChangedAt') toast.error('Enter a valid date.');
      else toast.error('Couldn\'t log the change. Please try again.');
    } finally {
      setLogBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await axiosInstant.post(interventionExpectRemovalEndpoint(removing.id), {});
      toast.success('Noted — the next page check confirms the removal.');
      setRemoving(null);
      await reload(true);
    } catch (e: any) {
      if (apiErrorCode(e) === 'NotAVideo') toast.error('Only videos can be reported as removed.');
      else toast.error('Couldn\'t record this. Please try again.');
    } finally {
      setRemoveBusy(false);
    }
  };

  // Observations: the raw looks at one page (analysis, page checks, script tag).
  const [obsPage, setObsPage] = useState<string>('');
  const [obsOpen, setObsOpen] = useState(false);
  const [obsRows, setObsRows] = useState<PageObservation[] | null>(null);
  const [obsLoading, setObsLoading] = useState(false);
  const loadObservations = async (pageId: string) => {
    if (!pageId) return;
    setObsLoading(true);
    try {
      const { data: rows } = await axiosInstant.get<PageObservation[]>(productAutopilotPageObservationsEndpoint(desireid, Number(pageId), brandId));
      setObsRows(rows || []);
    } catch {
      toast.error('Couldn\'t load the page checks.');
      setObsRows([]);
    } finally {
      setObsLoading(false);
    }
  };
  const toggleObservations = () => {
    const next = !obsOpen;
    setObsOpen(next);
    if (next) {
      const pid = obsPage || (pages.length ? String(pages[0].id) : '');
      if (pid !== obsPage) setObsPage(pid);
      if (pid) loadObservations(pid);
    }
  };

  if (loading) return <DotLottleLoader fill={false} />;

  if (error) {
    return (
      <Box className="flex flex-col items-start gap-2 pt-4">
        <p className="text-[13px] text-down m-0">{error}</p>
        <button type="button" className={btnSecondary} onClick={() => reload(false)}>
          Try again
        </button>
      </Box>
    );
  }

  return (
    <div className="flex flex-col gap-3 pt-4">
      <Box className="flex items-center justify-between gap-2">
        <h4 className="text-[14px] font-semibold text-text-1 m-0">Timeline</h4>
        <button type="button" className={btnPrimary} onClick={openLog} disabled={!pages.length} title={pages.length ? 'Record a change you made to a product page' : 'Add a product page first'}>
          Log a change
        </button>
      </Box>

      {interventions.length ? (
        <Box className="flex flex-col gap-2">
          {timeline.map((item) =>
            item.kind === 'episode' ? (
              <EpisodeCard key={item.episode.key} episode={item.episode} members={item.members} onRemove={setRemoving} />
            ) : (
              <InterventionRow key={item.iv.id} iv={item.iv} onRemove={setRemoving} />
            ),
          )}
        </Box>
      ) : (
        <Box className="rounded-[12px] border border-dashed border-border-token bg-surface-2 p-4 text-center">
          <p className="text-[12.5px] text-muted m-0">Nothing has changed on this product yet.</p>
        </Box>
      )}

      {pages.length ? (
        <Box className="mt-1">
          <button type="button" className={classNames(btnGhost, 'inline-flex items-center gap-1')} onClick={toggleObservations}>
            Page checks
            <IconChevronDown size={12} open={obsOpen} />
          </button>
          {obsOpen ? (
            <Box className="flex flex-col gap-2 mt-2">
              {pages.length > 1 ? (
                <TextField
                  select
                  size="small"
                  label="Product page"
                  value={obsPage}
                  onChange={(e) => {
                    setObsPage(e.target.value);
                    loadObservations(e.target.value);
                  }}
                  className="max-w-[320px]"
                >
                  {pages.map((pg) => (
                    <MenuItem key={pg.id} value={String(pg.id)}>
                      {pg.hostname}
                      {pg.retailer ? ` · ${pg.retailer}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {obsLoading ? <p className="text-[12px] text-muted m-0">Loading…</p> : null}
              {!obsLoading && obsRows && !obsRows.length ? <p className="text-[12px] text-muted m-0">No looks at this page yet.</p> : null}
              {!obsLoading && obsRows && obsRows.length ? (
                <ul className="m-0 pl-0 list-none flex flex-col gap-[2px] text-[12px] text-text-2">
                  {obsRows.map((o) => (
                    <li key={o.id} className="flex items-center gap-2 flex-wrap">
                      <span className="text-muted">{dayjs(o.observed_at).format('D MMM YYYY HH:mm')}</span>
                      <Pill tone={o.suspect ? 'warn' : 'neutral'} title={o.suspect ? 'This look hit a bot wall or an empty page and was not compared' : undefined}>
                        {humanize(o.source, OBSERVATION_SOURCE_LABELS)}
                      </Pill>
                      <span>
                        {o.videos.length} video{o.videos.length === 1 ? '' : 's'}
                        {o.our_file_ids.length ? ` · ${o.our_file_ids.length} expert` : ''}
                      </span>
                      {o.json_ld_on_page ? <span className="text-muted">markup on page</span> : null}
                      {o.scraper_tier ? <span className="text-muted">{o.scraper_tier}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}

      <ConfirmationDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={confirmRemove}
        title="You removed this video?"
        description="We record that you took it off the page. The next page check confirms it, and the removal is measured like any other change."
        confirmLabel={removeBusy ? 'Saving…' : 'Yes, I removed it'}
        disabled={removeBusy}
        maxWidth="xs"
      />

      <Dialog open={logOpen} onClose={() => (logBusy ? undefined : setLogOpen(false))} fullWidth maxWidth="xs">
        <DialogTitle className="!text-[16px] !font-semibold">Log a change</DialogTitle>
        <DialogContent className="flex flex-col gap-3 !pt-2">
          <p className="text-[12.5px] text-muted m-0">
            Tell Autopilot about a change you made to a product page outside the portal, so its measurements account for it.
          </p>
          <TextField select size="small" label="Product page" value={logPage} onChange={(e) => setLogPage(e.target.value)} fullWidth>
            {pages.map((pg) => (
              <MenuItem key={pg.id} value={String(pg.id)}>
                {pg.hostname}
                {pg.retailer ? ` · ${pg.retailer}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="What changed"
            value={logNote}
            onChange={(e) => setLogNote(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            placeholder="e.g. Replaced the hero image and rewrote the bullets"
          />
          <TextField
            size="small"
            label="When"
            type="date"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions className="!px-6 !pb-4">
          <Button variant="outlined" onClick={() => setLogOpen(false)} disabled={logBusy} className={classNames('!capitalize')}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={submitLog} disabled={logBusy} className="!capitalize">
            {logBusy ? 'Saving…' : 'Log change'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
